// Extract the prompt variables exposed by a Visual JSON so the dashboard
// designer can show each tile's criteria and let the author bind them
// to global criteria (or pin constants).
//
// A visual surfaces variables in two places, depending on DATA_SOURCE_MODE:
//   • 'sqlmodel' → SQLMODEL.CRITERIA.query.rules[].field is the variable
//   • 'sql'      → SOURCE_SQL contains ?.<var> placeholders (and
//                  SQLMODEL.CRITERIA.query.rules typically mirrors them)
// We read both and union the set so a partially-filled visual still
// shows its variables.
//
// `?.@TokenName` placeholders are SYSTEM inputs (LoginEmployee, Today,
// etc.) — they never prompt the user and can't be remapped, so we skip
// them.

export interface VisualParam {
  name: string                          // variable name (e.g. "from_date")
  promptLabel: string                   // human label
  dataType: 'string' | 'number' | 'date'
  operator: string                      // e.g. "=", "between", "in"
}

const SQL_VAR_RE = /\?\.([A-Za-z_][A-Za-z0-9_]*)/g

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
}

// Canonical UPPER_SNAKE key — mirrors the backend's `_norm_param`. Used only
// to dedupe / match a SOURCE_SQL placeholder against its criterion metadata;
// the param's displayed `name` keeps the author's original casing.
function normParam(name: unknown): string {
  return (typeof name === 'string' ? name : '').trim().replace(/ /g, '_').toUpperCase()
}

// Loose key for LINKING a SOURCE_SQL placeholder to its criterion — mirrors the
// backend's `_match_key`. Collapses every run of non-alphanumeric chars to one
// underscore and lowercases, so `?.as_on_date` matches a criterion labelled
// "As-on Date" (hyphen) or named after the column CUSTOMER_CODE. Used ONLY for
// metadata lookup, never as the emitted param name.
function matchKey(name: unknown): string {
  return (typeof name === 'string' ? name : '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function normalizeDataType(raw: unknown): 'string' | 'number' | 'date' {
  if (typeof raw !== 'string') return 'string'
  const u = raw.toUpperCase()
  if (u === 'NUMBER' || u.startsWith('NUM') || u === 'INT' || u.startsWith('DEC')) {
    return 'number'
  }
  if (u === 'DATE' || u === 'DATETIME' || u === 'TIMESTAMP') return 'date'
  return 'string'
}

interface RuleMeta { promptLabel: string; dataType: 'string' | 'number' | 'date'; operator: string }

export function extractVisualParams(content: unknown): VisualParam[] {
  const root = asRecord(content)
  if (!root) return []

  // Mirror the backend's `extract_prompt_vars` precisely so the mapping UI
  // offers exactly the params the engine will bind — no more, no less:
  //   • only CRITERIA rules with queryOption === 'P' count as prompts
  //   • the SOURCE OF TRUTH depends on DATA_SOURCE_MODE:
  //       'sql'      → the SOURCE_SQL ?.<var> placeholders
  //       'sqlmodel' → one param per prompt rule (keyed by field/label)
  // (The old impl unioned all three sources, so a single `between` criterion
  //  — column rule + ?.from_date + ?.to_date placeholders — surfaced THREE
  //  targets instead of the two the engine actually binds.)

  // ── Gather CRITERIA leaf rules, recursively (walk order = engine order). ──
  const sqlmodel = asRecord(root.SQLMODEL)
  const criteria = asRecord(sqlmodel?.CRITERIA)
  const query = asRecord(criteria?.query)
  const leafRules: Record<string, unknown>[] = []
  const walk = (group: Record<string, unknown> | null | undefined) => {
    if (!group) return
    const rules = Array.isArray(group.rules) ? (group.rules as unknown[]) : []
    for (const r of rules) {
      const o = asRecord(r)
      if (!o) continue
      if (Array.isArray(o.rules) && 'condition' in o) walk(o)             // nested group
      else leafRules.push(o)
    }
  }
  walk(query)

  // A BETWEEN rule with first-class `operands` surfaces one SCALAR param per
  // prompt-sourced bound (mirror of bi_sql.source_sql.is_operand_between).
  const isOperandBetween = (r: Record<string, unknown>): boolean => {
    const ops = r.operands
    return (
      String(r.operator ?? '').trim().toLowerCase() === 'between'
      && Array.isArray(ops) && ops.length === 2
      && ops.every((o) => !!asRecord(o))
    )
  }

  // Classic prompt rules (queryOption 'P'); operand-BETWEEN handled per-bound.
  const critRules = leafRules.filter(
    (o) => String(o.queryOption ?? '').trim().toUpperCase() === 'P' && !isOperandBetween(o),
  )

  const ruleMeta = (r: Record<string, unknown>): RuleMeta => ({
    promptLabel: (typeof r.promptLabel === 'string' && r.promptLabel.trim()) || '',
    dataType: normalizeDataType(r.COLTYPE ?? r.COLUMN_TYPE ?? r.currentFieldType),
    operator: typeof r.operator === 'string' ? r.operator : '=',
  })

  // Metadata lookup keyed by field, promptLabel AND DBNAME — under BOTH the
  // canonical name and a loose match key — so a raw placeholder like
  // ?.as_on_date finds a criterion labelled "As-on Date" (hyphen) or named
  // after the column CUSTOMER_CODE, and the prompt keeps its real data type.
  const metaByKey = new Map<string, RuleMeta>()
  for (const r of critRules) {
    const meta = ruleMeta(r)
    for (const raw of [r.field, r.promptLabel, r.DBNAME]) {
      for (const key of [normParam(raw), matchKey(raw)]) {
        if (key && !metaByKey.has(key)) metaByKey.set(key, meta)
      }
    }
  }

  const found = new Map<string, VisualParam>()
  const add = (rawName: string, meta?: Partial<RuleMeta>) => {
    const name = (rawName || '').trim()
    if (!name || name.startsWith('@')) return                 // system input
    const key = normParam(name)
    if (!key || found.has(key)) return
    found.set(key, {
      name,
      promptLabel: (meta?.promptLabel && meta.promptLabel.trim()) || titleCase(name),
      dataType: meta?.dataType || 'string',
      operator: meta?.operator || '=',
    })
  }

  const mode = String(root.DATA_SOURCE_MODE ?? 'sqlmodel').trim().toLowerCase()
  if (mode === 'sql') {
    // Bind variables ARE the SOURCE_SQL ?.<var> placeholders (deduped, in
    // order), enriched from a matching criterion when one exists.
    const sql = typeof root.SOURCE_SQL === 'string' ? root.SOURCE_SQL : ''
    let m: RegExpExecArray | null
    SQL_VAR_RE.lastIndex = 0
    while ((m = SQL_VAR_RE.exec(sql)) !== null) {
      add(m[1], metaByKey.get(normParam(m[1])) ?? metaByKey.get(matchKey(m[1])))
    }
  } else {
    // One param per prompt rule, keyed by its field (or label). Mirrors the
    // backend's unique_prompt_name: two prompt rules on the SAME column (a
    // >= / <= date-range pair) must surface as TWO params — on a name
    // collision the promptLabel disambiguates, then a numeric suffix.
    const used = new Set<string>()
    const uniqueName = (field: string, label: string): string => {
      const base = normParam(field) || normParam(label) || 'PARAM'
      let name = base
      if (used.has(name)) {
        const lbl = normParam(label)
        if (lbl && lbl !== base && !used.has(lbl)) {
          name = lbl
        } else {
          let n = 2
          while (used.has(`${base}_${n}`)) n++
          name = `${base}_${n}`
        }
      }
      used.add(name)
      return name
    }
    for (const r of leafRules) {
      if (isOperandBetween(r)) {
        // Mirror of the backend's between_operand_prompt: FIELD_FROM /
        // FIELD_TO, one scalar param per PROMPT-sourced bound.
        const field = typeof r.field === 'string' ? r.field.trim() : ''
        const ruleLabel = (typeof r.promptLabel === 'string' && r.promptLabel.trim()) || field || 'param'
        const ops = r.operands as unknown[]
        ops.forEach((raw, pos) => {
          const opnd = asRecord(raw)
          if (!opnd) return
          if (String(opnd.source ?? '').trim().toLowerCase() !== 'prompt') return
          const suffix = pos === 0 ? 'From' : 'To'
          const label = (typeof opnd.promptLabel === 'string' && opnd.promptLabel.trim())
            || `${ruleLabel} ${suffix}`
          const name = uniqueName(field ? `${field}_${suffix.toUpperCase()}` : '', label)
          add(name, {
            promptLabel: label,
            dataType: normalizeDataType(r.COLTYPE ?? r.COLUMN_TYPE ?? r.currentFieldType),
            operator: pos === 0 ? '>=' : '<=',
          })
        })
        continue
      }
      if (String(r.queryOption ?? '').trim().toUpperCase() !== 'P') continue
      const field = typeof r.field === 'string' ? r.field.trim() : ''
      const label = typeof r.promptLabel === 'string' ? r.promptLabel.trim() : ''
      add(uniqueName(field, label), ruleMeta(r))
    }
  }

  // Fallback ONLY: transaction-authored visuals expose their criteria under
  // transaction.sql_models[].sql_model.criteria[] instead of SQLMODEL /
  // SOURCE_SQL. Consult this just when the standard extraction found nothing,
  // so a normal `between` criterion is never double-counted.
  if (found.size === 0) {
    const tx = asRecord(root.transaction)
    const sqlModels = Array.isArray(tx?.sql_models) ? (tx!.sql_models as unknown[]) : []
    for (const sm of sqlModels) {
      const wrap = asRecord(sm)
      const inner = asRecord(wrap?.sql_model)
      const cl = Array.isArray(inner?.criteria) ? (inner!.criteria as unknown[]) : []
      for (const c of cl) {
        const wrapped = asRecord(c)
        if (!wrapped) continue
        const inner2 = asRecord(wrapped.column ?? wrapped.criterion)
        if (!inner2) continue
        const name = typeof inner2.name === 'string' ? inner2.name : ''
        if (!name) continue
        add(name, {
          promptLabel:
            (typeof inner2.heading === 'string' && inner2.heading)
              ? inner2.heading
              : (typeof inner2.descr === 'string' && inner2.descr)
                ? inner2.descr
                : titleCase(name),
          dataType: normalizeDataType(inner2.col_type ?? inner2.coltype),
          operator: typeof inner2.operator === 'string' ? inner2.operator : '=',
        })
      }
    }
  }

  return Array.from(found.values())
}

function titleCase(s: string): string {
  return s
    .replace(/[_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}
